import { history } from '../redux/lib/configureStore.js';
import selectors from 'selectors/selectors.js';
import getDefaultUserRoute from 'lib/getDefaultUserRoute.js';
import { ModifyContextState } from 'context/toastContext.js';
import { matchPermissionsToRoute } from 'lib/constants/permissions';
import { getServiceProvider } from '../redux/actions/serviceProviderActions';
import jwtDecode from 'jwt-decode';
import { matchPath } from 'react-router';

const userCanSeeServiceProvider = (pathname, serviceProviderIds) => {
  const pathnameServiceProviderId = pathname.params.serviceProviderId;
  if (isNaN(pathnameServiceProviderId)) {
    return true;
  }
  return (serviceProviderIds || []).includes(pathnameServiceProviderId);
};

const checkLoginRedirect = ({ getState, defaultPathname, isTresio = false }) => {
  const state = getState();
  const urlRedirectPath = state.app.redirectPath;

  if (isTresio && urlRedirectPath) {
    return urlRedirectPath;
  }
  if (!urlRedirectPath) {
    return defaultPathname;
  }

  const allPermissions = selectors.serviceProviderPermissions(state);
  const permissionRouteMatch = matchPermissionsToRoute(urlRedirectPath);
  const isPermitted = (allPermissions || []).find((n) => n === (permissionRouteMatch || {}).permission);
  return isPermitted ? urlRedirectPath : defaultPathname;
};

const getDefaultRoute = (state) => {
  const serviceProviderId = selectors.serviceProviderId(state);
  const uServiceProviders = selectors.userServiceProviders(state);
  const currentServiceProvider = selectors.serviceProvider(state);
  const tresioUser = state.user.tresioUser;
  if (!serviceProviderId) {
    return null;
  }
  return getDefaultUserRoute(serviceProviderId, uServiceProviders, currentServiceProvider, tresioUser);
};

const getDefaultRouteRoute = ({ getState }) => {
  const state = getState();
  const defaultRoute = getDefaultRoute(state);
  return defaultRoute;
};

export const permissionsMiddleware =
  ({ getState, dispatch }) =>
  (next) =>
  (action) => {
    if (action.type === 'GET_IMPERSONATION_TOKEN_SUCCESS') {
      let jwt = jwtDecode(action.payload.data.token);
      const serviceProviderId = jwt.service_provider_ids[0];
      next(action);
      getServiceProvider(serviceProviderId, () => {
        const defaultRoute = getDefaultRouteRoute({ getState });
        history.push(defaultRoute);
      });
    }
    if (action.type === '@@router/LOCATION_CHANGE') {
      const state = getState();
      const tresioUser = state.user.tresioUser;

      if (tresioUser) {
        return next(action);
      }

      const pathname = action.payload.location.pathname;
      const user = state.user;
      const { serviceProviderIds } = user;
      // Dashboard redirects
      if ((pathname || ' ').match(/^\/dashboard/g)) {
        if ((serviceProviderIds || []).length > 1) {
          return next(action);
        } else {
          const defaultRoute = getDefaultRouteRoute({ getState });
          action.payload.location.pathname = defaultRoute;
          history.replace(defaultRoute);
        }
      }
      const permissionRouteMatch = matchPermissionsToRoute(pathname);
      if (!permissionRouteMatch) {
        return next(action);
      }
      const allPermissions = selectors.serviceProviderPermissions(state);
      const matchPathname = matchPath(action.payload.location.pathname, { path: '/:serviceProviderId' });

      const isPermitted = (allPermissions || []).find((n) => n === (permissionRouteMatch || {}).permission) && userCanSeeServiceProvider(matchPathname, serviceProviderIds);

      const defaultRoute = getDefaultRoute(state);
      if (isPermitted) {
        return next(action);
      }
      // Not permitted. Modify location pathname and set url
      if (defaultRoute) {
        if (ModifyContextState) {
          ModifyContextState.setToastMessage('Permission Denied');
        }
        action.payload.location.pathname = defaultRoute;
        history.replace(defaultRoute);
      }
    }
    if (action.type === 'LOGIN_TRESIO_USER') {
      history.push(checkLoginRedirect({ getState, defaultPathname: `/dashboard/service-providers`, isTresio: true }));
      dispatch({ type: 'SET_REDIRECT_FROM_LOGIN_PATH', payload: null });
    }
    if (action.type === 'LOGIN_STANDARD_USER') {
      const defaultRoute = getDefaultRouteRoute({ getState });
      history.push(checkLoginRedirect({ getState, defaultPathname: defaultRoute ? defaultRoute : `/${action.payload}/inbox/inbox` }));
      dispatch({ type: 'SET_REDIRECT_FROM_LOGIN_PATH', payload: null });
    }
    next(action);
  };
